import { Observable } from 'rxjs';
import {
  CompanyType,
  ContactPerson,
  IdentifierName,
  Locale,
  Organization,
  Identifier,
  OrganizationInput,
} from '@iss/libs/graphql';
import { FormControl, Validators } from '@angular/forms';
import { DestroyRef } from '@angular/core';

import { ContactPeopleFormArray } from './contact-people.form-array';
import { AddressFormGroup } from './address-form.group';

import { DeepPartial } from '@iss/wo-common/types';
import { ControlsOf, ReactiveFormGroup } from '@iss/core/forms';

export const MAX_COMMENT_TEXT = 255;

const eightDigitsValidator = Validators.pattern(/^\d{8}$/);
const pNumberDKValidator = [ Validators.required, Validators.pattern(/^\d{10}$/) ];

export type OrganizationControls =
  Pick<ControlsOf<OrganizationInput>, 'name' | 'companyType' | 'comment' | 'vatIdNumber' | 'paymentAccountNumber' | 'coupaNumber'>
  & Readonly<{
    geographicAddresses: AddressFormGroup;
    pNumber: FormControl<string | undefined>;
    seNumber: FormControl<string | undefined>;
    contactPeople: ContactPeopleFormArray;
  }>;

export class OrganizationFormGroup extends ReactiveFormGroup<OrganizationControls> {
  public static create(
    destroyRef: DestroyRef,
    value?: DeepPartial<Organization>,
    newContactPersonValue?: DeepPartial<ContactPerson>,
    requiredRoles?: readonly string[],
  ): OrganizationFormGroup {
    const pNumber = value?.identifiers
      ?.find(({ name }: Identifier) => name === IdentifierName.PNumber)
      ?.value;

    const seNumber = value?.identifiers
      ?.find(({ name }: Identifier) => name === IdentifierName.SeNumber)
      ?.value;

    return new this({
      name: new FormControl(value?.name || '', {
        nonNullable: true,
        validators: [ Validators.required, Validators.maxLength(255) ],
      }),
      comment: new FormControl(value?.comment, {
        nonNullable: true,
        validators: [ Validators.maxLength(MAX_COMMENT_TEXT) ],
      }),
      companyType: new FormControl(value?.companyType || CompanyType.Private, {
        nonNullable: true,
        validators: [ Validators.required ],
      }),
      geographicAddresses: AddressFormGroup.create(destroyRef, value?.geographicAddresses),
      vatIdNumber: new FormControl(value?.vatIdNumber || '', {
        nonNullable: true,
        validators: [ Validators.required, Validators.maxLength(255) ],
      }),
      coupaNumber: new FormControl(value?.coupaNumber, {
        nonNullable: true,
        validators: [ Validators.maxLength(255) ],
      }),
      paymentAccountNumber: new FormControl(value?.paymentAccountNumber || '', {
        nonNullable: true,
        validators: [ Validators.required, Validators.maxLength(255) ],
      }),
      pNumber: new FormControl(pNumber, { nonNullable: true, validators: [ Validators.maxLength(255) ] }),
      seNumber: new FormControl(seNumber, { nonNullable: true, validators: [ Validators.maxLength(255) ] }),
      contactPeople: ContactPeopleFormArray.create(value?.contactPeople, newContactPersonValue, requiredRoles),
    }, { destroyRef });
  }

  protected woMonitor(): Observable<unknown> {
    return this.controls.geographicAddresses.controls.locale.valueChanges;
  }

  protected woDoCheck(): void {
    const isDenmark = Locale.Dk === this.controls.geographicAddresses.controls.locale.value;

    this
      .woEnableValidators(this.controls.vatIdNumber, eightDigitsValidator, isDenmark)
      .woEnableValidators(this.controls.pNumber, pNumberDKValidator, isDenmark)
    ;
  }
}
